/**
 * Drives the procedure in the twin store: on every step it applies the
 * choreography (asset switch, view mode and signal path, subview, selection
 * of the first highlighted subsystem, camera) and, while playing, advances
 * after the step's dwell. Mounted once by TwinHud; the panel and the HUD
 * strip only send play, pause, previous and next.
 */
import { useEffect, useRef } from 'react';
import { useUI, clockNow, useClock } from '@/state/store';
import { usePresenter } from '@/features/presenter/store';
import { useTwin } from '../../store';
import type { AssetSpec, ProcedureStep } from '../../types';
import { DEFAULT_DWELL_MS } from './loadProcedures';
import { sceneStore } from '../../scene/sceneStore';
import { useSatelliteOrbit } from '../../orbit/store';

/** Subview id for a step hint: an exact id, else the first subview whose id or label contains the hint. */
export function resolveSubview(asset: AssetSpec | null, hint: string | undefined): string | null {
  if (!asset?.subviews?.length || !hint) return null;
  const exact = asset.subviews.find((s) => s.id === hint);
  if (exact) return exact.id;
  const needle = hint.toLowerCase().replace(/[-_]/g, ' ');
  const loose = asset.subviews.find((s) =>
    `${s.id} ${s.label}`.toLowerCase().replace(/[-_]/g, ' ').includes(needle.split(' ')[0]),
  );
  return loose?.id ?? null;
}

/** Signal path id for a step: an exact id, else the first path with that direction. */
export function resolveSignalPath(
  asset: AssetSpec | null,
  hint: string | null | undefined,
  subview?: string | null,
): string | null {
  const paths = asset?.subviews?.find((v) => v.id === subview)?.signalPaths ?? asset?.signalPaths;
  if (!paths?.length || !hint) return null;
  const exact = paths.find((p) => p.id === hint);
  if (exact) return exact.id;
  return paths.find((p) => p.direction === hint)?.id ?? null;
}

export function applyStep(step: ProcedureStep, procedureAssetId: string): void {
  const twin = useTwin.getState();
  const ui = useUI.getState();
  const targetAsset = step.assetId ?? procedureAssetId;
  if (ui.selection.assetId !== targetAsset) {
    ui.select({ assetId: targetAsset, subsystemId: null });
    return; // the runtime loads the asset; the effect runs again when it is ready
  }
  if (twin.loadStatus !== 'ready' || twin.assetId !== targetAsset) return;
  const subview = resolveSubview(twin.asset, step.subview);
  const signalPath = resolveSignalPath(twin.asset, step.signalPathId, subview);
  if (signalPath) twin.setSignalPath(signalPath);
  else if (twin.activeSignalPathId) twin.setSignalPath(null);
  if (subview !== twin.subviewId) twin.setSubview(subview);
  // Some imported paths are also visible in solid mode; the procedure still
  // owns its authored mode, independently of the path picker's default.
  if (step.viewMode) twin.setViewMode(step.viewMode);
  const first =
    step.highlight?.[0] ?? (step.camera && 'subsystemId' in step.camera ? step.camera.subsystemId : null);
  if (first) {
    ui.select({ subsystemId: first });
  } else if (ui.selection.subsystemId) {
    ui.select({ subsystemId: null });
  }
  if (step.camera && 'position' in step.camera) twin.requestCamera({ pose: step.camera });
  else if (step.camera && 'subsystemId' in step.camera)
    twin.requestCamera({ subsystemId: step.camera.subsystemId });
  else
    twin.requestCamera({
      poseId: step.camera && 'poseId' in step.camera ? step.camera.poseId : (subview ?? 'main'),
    });
  if (step.orbitView) {
    useSatelliteOrbit.getState().setView(step.orbitView);
    useSatelliteOrbit.getState().setBeam(true);
  }
}

export function useProcedureRunner(): void {
  const procedure = useTwin((s) => s.procedure);
  const loadStatus = useTwin((s) => s.loadStatus);
  const twinAssetId = useTwin((s) => s.assetId);
  const selectedAsset = useUI((s) => s.selection.assetId);
  const applied = useRef<string>('');
  const dwell = useRef({ key: '', elapsed: 0 });

  const id = procedure?.procedure.id ?? '';
  const stepIndex = procedure?.stepIndex ?? -1;
  const playing = procedure?.playing ?? false;
  const revision = procedure?.revision ?? 0;

  // Choreography once per (procedure, step, loaded asset).
  useEffect(() => {
    if (!procedure) {
      if (applied.current) {
        const twin = useTwin.getState();
        twin.setSignalPath(null);
        twin.setHovered(null);
        useUI.getState().select({ subsystemId: null });
      }
      applied.current = '';
      return;
    }
    const step = procedure.procedure.steps[procedure.stepIndex];
    if (!step) return;
    const key = `${id}#${stepIndex}#${revision}#${twinAssetId}#${loadStatus}#${selectedAsset}`;
    if (applied.current === key) return;
    applied.current = key;
    applyStep(step, procedure.procedure.assetId);
  }, [procedure, id, stepIndex, revision, twinAssetId, loadStatus, selectedAsset]);

  // Dwell: advance while playing; stop at the end.
  useEffect(() => {
    if (!procedure) {
      dwell.current = { key: '', elapsed: 0 };
      return;
    }
    const key = `${id}#${stepIndex}#${revision}`;
    if (dwell.current.key !== key) dwell.current = { key, elapsed: 0 };
    if (!playing) return;
    const step = procedure.procedure.steps[procedure.stepIndex];
    const last = procedure.stepIndex >= procedure.procedure.steps.length - 1;
    let previous = clockNow();
    const timer = window.setInterval(() => {
      const now = clockNow();
      const delta = now - previous;
      previous = now;
      if (usePresenter.getState().frozen || !useClock.getState().playing) return;
      const current = useTwin.getState();
      const target = step?.assetId ?? procedure.procedure.assetId;
      const scene = sceneStore.getState();
      // Loading a new asset/view and camera travel are not reading time.
      if (
        current.loadStatus !== 'ready' ||
        current.assetId !== target ||
        !scene.root ||
        scene.view !== (current.subviewId ?? 'main') ||
        scene.flight
      )
        return;
      if (delta >= 0 && delta < 60_000) dwell.current.elapsed += delta;
      if (dwell.current.elapsed < (step?.dwellMs ?? DEFAULT_DWELL_MS)) return;
      const s = useTwin.getState();
      if (!s.procedure || s.procedure.procedure.id !== id || s.procedure.stepIndex !== stepIndex) return;
      if (last) s.setProcedurePlaying(false);
      else s.setProcedureStep(stepIndex + 1);
    }, 100);
    return () => window.clearInterval(timer);
  }, [procedure, playing, id, stepIndex, revision]);
}
